// src/components/Loading.jsx
import { useState, useEffect, useRef } from 'react'

const Loading = ({ onLoadingComplete }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [shouldShow, setShouldShow] = useState(true)
  const [loadingStep, setLoadingStep] = useState(0)
  const [resourcesLoaded, setResourcesLoaded] = useState(false)
  const [reactLoaded, setReactLoaded] = useState(false)
  const loadingRef = useRef(null)

  const loadingSteps = [
    "Initializing...",
    "Loading resources...", 
    "Preparing your experience...",
    "Almost ready..."
  ]

  // Function to check if all images are loaded
  const checkImagesLoaded = () => {
    const images = document.images
    const totalImages = images.length
    let loadedImages = 0

    if (totalImages === 0) {
      setResourcesLoaded(true)
      return
    }

    Array.from(images).forEach(img => {
      if (img.complete) {
        loadedImages++
      } else {
        img.addEventListener('load', () => {
          loadedImages++
          if (loadedImages === totalImages) {
            setResourcesLoaded(true)
          }
        })
        img.addEventListener('error', () => {
          loadedImages++ // Count errors as loaded to prevent infinite loading
          if (loadedImages === totalImages) {
            setResourcesLoaded(true)
          }
        })
      }
    })

    // Check if all images are already loaded
    if (loadedImages === totalImages) {
      setResourcesLoaded(true)
    }
  }

  // Function to check if fonts are loaded
  const checkFontsLoaded = () => {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setResourcesLoaded(prev => prev) // Fonts are ready
      })
    }
  }

  useEffect(() => {
    setIsVisible(true)
    document.body.classList.add('loading-active')

    // Mark React as loaded after a short delay (React hydration complete)
    const reactTimer = setTimeout(() => {
      setReactLoaded(true)
    }, 100)

    // Check resources after React is loaded
    const resourceTimer = setTimeout(() => {
      checkImagesLoaded()
      checkFontsLoaded()
    }, 200)

    // Simulate progressive loading steps
    const stepInterval = setInterval(() => {
      setLoadingStep(prev => {
        if (prev >= loadingSteps.length - 1) {
          clearInterval(stepInterval)
          return prev
        }
        return prev + 1
      })
    }, 600)

    // Check if everything is ready
    const checkReady = setInterval(() => {
      if (reactLoaded && resourcesLoaded) {
        clearInterval(checkReady)
        
        // Add a small delay for smooth transition
        setTimeout(() => {
          setShouldShow(false)
          document.body.classList.remove('loading-active')
          if (onLoadingComplete) {
            onLoadingComplete()
          }
        }, 500)
      }
    }, 100)

    // Fallback: Maximum loading time of 8 seconds
    const maxLoadingTime = setTimeout(() => {
      setShouldShow(false)
      document.body.classList.remove('loading-active')
      if (onLoadingComplete) {
        onLoadingComplete()
      }
      console.warn('Loading timeout reached - showing page anyway')
    }, 8000)

    return () => {
      clearTimeout(reactTimer)
      clearTimeout(resourceTimer)
      clearInterval(stepInterval)
      clearInterval(checkReady)
      clearTimeout(maxLoadingTime)
      document.body.classList.remove('loading-active')
    }
  }, [reactLoaded, resourcesLoaded, loadingSteps.length, onLoadingComplete])

  // Update progress based on actual loading state
  useEffect(() => {
    if (resourcesLoaded && reactLoaded) {
      setLoadingStep(loadingSteps.length - 1)
    }
  }, [resourcesLoaded, reactLoaded, loadingSteps.length])

  if (!shouldShow) {
    return null
  }

  return (
    <div className={`loading-container ${isVisible ? 'visible' : ''}`} ref={loadingRef}>
      {/* Background Elements */}
      <div className="loading-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="loading-grid"></div>
      </div>
      
      {/* Main Content */}
      <div className="loading-content">
        {/* Enhanced Spinner */}
        <div className="loading-spinner-wrapper">
          <div className="loading-spinner">
            <div className="spinner-ring ring-1"></div>
            <div className="spinner-ring ring-2"></div>
            <div className="spinner-ring ring-3"></div>
            <div className="spinner-center">
              <span className="spinner-icon">🦷</span>
            </div>
          </div>
          <div className="spinner-glow"></div>
        </div>
        
        {/* Text Content */}
        <div className="loading-text-content">
          <h2 className="loading-text">
            <span className="text-gradient">Emmanuel</span>
            <span className="text-accent"> DentalCare</span>
          </h2>
          <p className="loading-subtext">Your smile is our priority</p>
          
          {/* Loading Step Indicator */}
          <div className="loading-step">
            <div className="step-dots">
              {loadingSteps.map((_, index) => (
                <div 
                  key={index}
                  className={`step-dot ${index <= loadingStep ? 'active' : ''}`}
                />
              ))}
            </div>
            <p className="step-text">
              {resourcesLoaded && reactLoaded 
                ? "Ready!" 
                : loadingSteps[loadingStep]
              }
            </p>
          </div>
        </div>
        
        {/* Progress Indicator */}
        <div className="loading-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                transform: `scaleX(${
                  (resourcesLoaded && reactLoaded) 
                    ? 1 
                    : (loadingStep + 1) / loadingSteps.length
                })`,
                transition: 'transform 0.5s ease'
              }}
            ></div>
          </div>
          <div className="progress-percentage">
            {resourcesLoaded && reactLoaded 
              ? "100%" 
              : `${Math.round(((loadingStep + 1) / loadingSteps.length) * 100)}%`
            }
          </div>
        </div>

        {/* Debug info (remove in production)
        {process.env.NODE_ENV === 'development' && (
          <div className="debug-info" style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            color: 'rgba(223, 112, 38, 0.7)',
            fontSize: '12px'
          }}>
            React: {reactLoaded ? '✅' : '⏳'} | 
            Resources: {resourcesLoaded ? '✅' : '⏳'}
          </div>
        )} */}
      </div>
    </div>
  )
}

export default Loading